import React, { useState, useEffect, useCallback } from 'react';
import { 
  ArrowUpRight, ArrowDownLeft, RefreshCcw, CreditCard, TrendingUp, 
  Building2, Filter, Calendar, Search, ChevronDown, ChevronLeft, 
  ChevronRight, Download, Clock, CheckCircle2, XCircle, AlertCircle,
  Wallet, ArrowRightLeft
} from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useTheme } from '../contexts/ThemeContext';
import axios from 'axios';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Transaction type icons and colors
const getTransactionIcon = (type) => {
  switch (type) {
    case 'crypto_transfer':
      return { icon: ArrowUpRight, color: 'text-blue-500', bg: 'bg-blue-500/10' };
    case 'fiat_transfer':
      return { icon: Building2, color: 'text-green-500', bg: 'bg-green-500/10' };
    case 'card_payment':
      return { icon: CreditCard, color: 'text-purple-500', bg: 'bg-purple-500/10' };
    case 'swap':
      return { icon: ArrowRightLeft, color: 'text-orange-500', bg: 'bg-orange-500/10' };
    case 'staking':
      return { icon: TrendingUp, color: 'text-cyan-500', bg: 'bg-cyan-500/10' };
    case 'deposit':
      return { icon: ArrowDownLeft, color: 'text-green-500', bg: 'bg-green-500/10' };
    case 'withdrawal':
      return { icon: ArrowUpRight, color: 'text-red-500', bg: 'bg-red-500/10' };
    default:
      return { icon: Wallet, color: 'text-gray-500', bg: 'bg-gray-500/10' };
  }
};

// Status badge component
const StatusBadge = ({ status }) => {
  const statusConfig = {
    completed: { icon: CheckCircle2, color: 'text-green-500', bg: 'bg-green-500/10', label: 'Completed' },
    pending: { icon: Clock, color: 'text-yellow-500', bg: 'bg-yellow-500/10', label: 'Pending' },
    failed: { icon: XCircle, color: 'text-red-500', bg: 'bg-red-500/10', label: 'Failed' },
    cancelled: { icon: XCircle, color: 'text-gray-500', bg: 'bg-gray-500/10', label: 'Cancelled' },
    active: { icon: TrendingUp, color: 'text-cyan-500', bg: 'bg-cyan-500/10', label: 'Active' }
  };

  const config = statusConfig[status] || statusConfig.pending;
  const Icon = config.icon;

  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${config.bg} ${config.color}`}>
      <Icon className="w-3 h-3" />
      {config.label}
    </span>
  );
};

// Transaction row component
const TransactionRow = ({ transaction, isDark }) => {
  const [expanded, setExpanded] = useState(false);
  const { icon: Icon, color, bg } = getTransactionIcon(transaction.type);
  
  const formatDate = (dateStr) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-US', { 
      month: 'short', 
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const formatAmount = (amount, currency) => {
    const isNegative = transaction.type.includes('transfer') || transaction.type === 'withdrawal' || transaction.type === 'card_payment';
    const prefix = isNegative ? '-' : '+';
    return `${prefix}${amount.toLocaleString()} ${currency}`;
  };

  const textPrimary = isDark ? 'text-white' : 'text-gray-900';
  const textMuted = isDark ? 'text-white/60' : 'text-gray-600';
  const borderColor = isDark ? 'border-white/10' : 'border-gray-200';

  return (
    <div className={`border-b ${borderColor} last:border-b-0`}>
      <div 
        className={`flex items-center justify-between p-4 cursor-pointer hover:${isDark ? 'bg-white/5' : 'bg-gray-50'} transition-colors`}
        onClick={() => setExpanded(!expanded)}
      >
        <div className="flex items-center gap-3">
          <div className={`w-10 h-10 rounded-full ${bg} flex items-center justify-center`}>
            <Icon className={`w-5 h-5 ${color}`} />
          </div>
          <div>
            <div className={`font-medium ${textPrimary}`}>{transaction.description}</div>
            <div className={`text-xs ${textMuted}`}>{formatDate(transaction.created_at)}</div>
          </div>
        </div>
        <div className="flex items-center gap-4">
          <div className="text-right">
            <div className={`font-mono font-medium ${
              transaction.type.includes('transfer') || transaction.type === 'withdrawal' || transaction.type === 'card_payment'
                ? 'text-red-500' 
                : 'text-green-500'
            }`}>
              {formatAmount(transaction.amount, transaction.currency)}
            </div>
            <StatusBadge status={transaction.status} />
          </div>
          <ChevronDown className={`w-4 h-4 ${textMuted} transition-transform ${expanded ? 'rotate-180' : ''}`} />
        </div>
      </div>
      
      {expanded && transaction.metadata && (
        <div className={`px-4 pb-4 pt-2 ${isDark ? 'bg-white/5' : 'bg-gray-50'}`}>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
            {transaction.metadata.to_address && (
              <div>
                <div className={`text-xs ${textMuted}`}>To Address</div>
                <div className={`font-mono ${textPrimary} truncate`}>{transaction.metadata.to_address}</div>
              </div>
            )}
            {transaction.metadata.network && (
              <div>
                <div className={`text-xs ${textMuted}`}>Network</div>
                <div className={`${textPrimary} capitalize`}>{transaction.metadata.network}</div>
              </div>
            )}
            {transaction.metadata.tx_hash && (
              <div>
                <div className={`text-xs ${textMuted}`}>TX Hash</div>
                <div className={`font-mono ${textPrimary} truncate`}>{transaction.metadata.tx_hash}</div>
              </div>
            )}
            {transaction.metadata.merchant && (
              <div>
                <div className={`text-xs ${textMuted}`}>Merchant</div>
                <div className={textPrimary}>{transaction.metadata.merchant}</div>
              </div>
            )}
            {transaction.metadata.from_token && (
              <div>
                <div className={`text-xs ${textMuted}`}>From</div>
                <div className={textPrimary}>{transaction.metadata.from_amount} {transaction.metadata.from_token}</div>
              </div>
            )}
            {transaction.metadata.to_token && (
              <div>
                <div className={`text-xs ${textMuted}`}>To</div>
                <div className={textPrimary}>{transaction.metadata.to_amount} {transaction.metadata.to_token}</div>
              </div>
            )}
            {transaction.metadata.apy && (
              <div>
                <div className={`text-xs ${textMuted}`}>APY</div>
                <div className="text-green-500">{transaction.metadata.apy}%</div>
              </div>
            )}
            {transaction.metadata.rewards_earned !== undefined && (
              <div>
                <div className={`text-xs ${textMuted}`}>Rewards Earned</div>
                <div className="text-green-500">+{transaction.metadata.rewards_earned}</div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

const TransactionHistory = () => {
  const { t } = useTranslation();
  const { isDark } = useTheme();
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState(null);
  
  // Filters
  const [activeTab, setActiveTab] = useState('all');
  const [statusFilter, setStatusFilter] = useState('');
  const [dateRange, setDateRange] = useState({ start: '', end: '' });
  const [searchQuery, setSearchQuery] = useState('');
  const [showFilters, setShowFilters] = useState(false);
  
  // Pagination
  const [pagination, setPagination] = useState({ total: 0, limit: 20, offset: 0, has_more: false });

  // Theme colors
  const textPrimary = isDark ? 'text-white' : 'text-gray-900';
  const textMuted = isDark ? 'text-white/60' : 'text-gray-600';
  const cardBg = isDark ? 'bg-black/40' : 'bg-white';
  const cardBorder = isDark ? 'border-white/10' : 'border-gray-200';
  const inputBg = isDark ? 'bg-white/5' : 'bg-gray-50';

  const getAuthHeader = useCallback(() => {
    const token = localStorage.getItem('token');
    return token ? { Authorization: `Bearer ${token}` } : {};
  }, []);

  const fetchTransactions = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (activeTab !== 'all') params.append('category', activeTab);
      if (statusFilter) params.append('status', statusFilter);
      if (dateRange.start) params.append('start_date', dateRange.start);
      if (dateRange.end) params.append('end_date', dateRange.end);
      params.append('limit', pagination.limit);
      params.append('offset', pagination.offset);

      const response = await axios.get(`${API}/transactions/history?${params.toString()}`, {
        headers: getAuthHeader()
      });

      setTransactions(response.data.transactions || []);
      setPagination(prev => ({
        ...prev,
        ...response.data.pagination
      }));
    } catch (error) {
      console.error('Error fetching transactions:', error);
      // Show demo data if API fails
      setTransactions(getDemoTransactions());
    } finally {
      setLoading(false);
    }
  }, [activeTab, statusFilter, dateRange, pagination.limit, pagination.offset, getAuthHeader]);

  const fetchStats = async () => {
    try {
      const response = await axios.get(`${API}/transactions/stats?period=30d`, {
        headers: getAuthHeader()
      });
      setStats(response.data.stats);
    } catch (error) {
      console.error('Error fetching stats:', error);
      // Demo stats
      setStats({
        total_transactions: 47,
        crypto_transactions: 23,
        banking_transactions: 15,
        card_transactions: 9,
        total_volume: 15420.50
      });
    }
  };

  useEffect(() => {
    fetchTransactions();
    fetchStats();
  }, [fetchTransactions]);

  const getDemoTransactions = () => [
    {
      id: '1',
      type: 'crypto_transfer',
      category: 'crypto',
      amount: 0.5,
      currency: 'ETH',
      status: 'completed',
      description: 'Transfer to 0x1234...5678',
      created_at: new Date(Date.now() - 1000 * 60 * 60 * 2).toISOString(),
      metadata: { to_address: '0x1234567890abcdef1234567890abcdef12345678', network: 'ethereum' }
    },
    {
      id: '2',
      type: 'swap',
      category: 'crypto',
      amount: 1000,
      currency: 'USDC',
      status: 'completed',
      description: 'Swap USDC → ETH',
      created_at: new Date(Date.now() - 1000 * 60 * 60 * 24).toISOString(),
      metadata: { from_token: 'USDC', to_token: 'ETH', from_amount: 1000, to_amount: 0.45 }
    },
    {
      id: '3',
      type: 'card_payment',
      category: 'cards',
      amount: 89.99,
      currency: 'USD',
      status: 'completed',
      description: 'Amazon.com',
      created_at: new Date(Date.now() - 1000 * 60 * 60 * 48).toISOString(),
      metadata: { merchant: 'Amazon.com', merchant_category: 'Shopping' }
    },
    {
      id: '4',
      type: 'fiat_transfer',
      category: 'banking',
      amount: 500,
      currency: 'EUR',
      status: 'pending',
      description: 'Transfer to John Doe',
      created_at: new Date(Date.now() - 1000 * 60 * 60 * 72).toISOString(),
      metadata: { to_address: 'DE89370400440532013000' }
    },
    {
      id: '5',
      type: 'staking',
      category: 'crypto',
      amount: 2.5,
      currency: 'ETH',
      status: 'active',
      description: 'Staking ETH at 4.5% APY',
      created_at: new Date(Date.now() - 1000 * 60 * 60 * 24 * 7).toISOString(),
      metadata: { token: 'ETH', apy: 4.5, rewards_earned: 0.012 }
    }
  ];

  const tabs = [
    { id: 'all', label: 'All', icon: RefreshCcw },
    { id: 'crypto', label: 'Crypto', icon: Wallet },
    { id: 'banking', label: 'Banking', icon: Building2 },
    { id: 'cards', label: 'Cards', icon: CreditCard }
  ];

  const filteredTransactions = searchQuery 
    ? transactions.filter(t => 
        t.description.toLowerCase().includes(searchQuery.toLowerCase()) ||
        t.currency.toLowerCase().includes(searchQuery.toLowerCase())
      )
    : transactions;

  const handlePageChange = (direction) => {
    setPagination(prev => ({
      ...prev,
      offset: direction === 'next' 
        ? prev.offset + prev.limit 
        : Math.max(0, prev.offset - prev.limit)
    }));
  };

  const exportTransactions = () => {
    const csv = [
      ['Date', 'Type', 'Description', 'Amount', 'Currency', 'Status'].join(','),
      ...filteredTransactions.map(t => [
        new Date(t.created_at).toISOString(),
        t.type,
        t.description,
        t.amount,
        t.currency,
        t.status
      ].join(','))
    ].join('\n');

    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `transactions_${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
    toast.success('Transactions exported');
  };

  return (
    <div data-testid="transaction-history-page" className="min-h-screen py-6 px-3 md:px-4">
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className={`text-2xl md:text-3xl font-bold ${textPrimary} mb-1`}>Transaction History</h1>
            <p className={`${textMuted} text-sm`}>View all your transactions in one place</p>
          </div>
          <button
            onClick={exportTransactions}
            className={`flex items-center gap-1.5 ${isDark ? 'bg-white/10 hover:bg-white/20' : 'bg-gray-100 hover:bg-gray-200'} ${textPrimary} text-sm font-medium px-4 py-2.5 rounded-full transition-all`}
          >
            <Download className="w-4 h-4" />
            Export
          </button>
        </div>

        {/* Stats Cards */}
        {stats && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
            <div className={`${cardBg} border ${cardBorder} rounded-xl p-4`}>
              <div className={`${textMuted} text-xs mb-1`}>Total Transactions</div>
              <div className={`text-2xl font-bold ${textPrimary}`}>{stats.total_transactions}</div>
            </div>
            <div className={`${cardBg} border ${cardBorder} rounded-xl p-4`}>
              <div className={`${textMuted} text-xs mb-1`}>Crypto</div>
              <div className={`text-2xl font-bold text-blue-500`}>{stats.crypto_transactions}</div>
            </div>
            <div className={`${cardBg} border ${cardBorder} rounded-xl p-4`}>
              <div className={`${textMuted} text-xs mb-1`}>Banking</div>
              <div className={`text-2xl font-bold text-green-500`}>{stats.banking_transactions}</div>
            </div>
            <div className={`${cardBg} border ${cardBorder} rounded-xl p-4`}>
              <div className={`${textMuted} text-xs mb-1`}>Card Payments</div>
              <div className={`text-2xl font-bold text-purple-500`}>{stats.card_transactions}</div>
            </div>
          </div>
        )}

        {/* Tabs & Filters */}
        <div className={`${cardBg} border ${cardBorder} rounded-2xl overflow-hidden`}>
          {/* Tab Navigation */}
          <div className={`flex items-center justify-between border-b ${cardBorder} px-4 py-3`}>
            <div className="flex gap-1">
              {tabs.map((tab) => {
                const Icon = tab.icon;
                return (
                  <button
                    key={tab.id}
                    onClick={() => {
                      setActiveTab(tab.id);
                      setPagination(prev => ({ ...prev, offset: 0 }));
                    }}
                    className={`flex items-center gap-1.5 px-4 py-2 rounded-full text-sm font-medium transition-all ${
                      activeTab === tab.id
                        ? 'bg-purple-600 text-white'
                        : isDark 
                          ? 'text-white/60 hover:bg-white/10'
                          : 'text-gray-600 hover:bg-gray-100'
                    }`}
                  >
                    <Icon className="w-4 h-4" />
                    {tab.label}
                  </button>
                );
              })}
            </div>
            <button
              onClick={() => setShowFilters(!showFilters)}
              className={`flex items-center gap-1.5 px-3 py-2 rounded-lg text-sm ${
                showFilters 
                  ? 'bg-purple-600 text-white' 
                  : isDark ? 'text-white/60 hover:bg-white/10' : 'text-gray-600 hover:bg-gray-100'
              }`}
            >
              <Filter className="w-4 h-4" />
              Filters
            </button>
          </div>

          {/* Filter Panel */}
          {showFilters && (
            <div className={`p-4 border-b ${cardBorder} ${isDark ? 'bg-white/5' : 'bg-gray-50'}`}>
              <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                {/* Search */}
                <div className="relative">
                  <Search className={`absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 ${textMuted}`} />
                  <input
                    type="text"
                    placeholder="Search transactions..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className={`w-full ${inputBg} border ${cardBorder} rounded-lg pl-10 pr-4 py-2 ${textPrimary} text-sm focus:border-purple-500 focus:outline-none`}
                  />
                </div>

                {/* Status Filter */}
                <select
                  value={statusFilter}
                  onChange={(e) => {
                    setStatusFilter(e.target.value);
                    setPagination(prev => ({ ...prev, offset: 0 }));
                  }}
                  className={`${inputBg} border ${cardBorder} rounded-lg px-4 py-2 ${textPrimary} text-sm focus:border-purple-500 focus:outline-none`}
                >
                  <option value="">All Status</option>
                  <option value="completed">Completed</option>
                  <option value="pending">Pending</option>
                  <option value="failed">Failed</option>
                </select>

                {/* Date Range */}
                <div className="flex gap-2">
                  <input
                    type="date"
                    value={dateRange.start}
                    onChange={(e) => {
                      setDateRange(prev => ({ ...prev, start: e.target.value }));
                      setPagination(prev => ({ ...prev, offset: 0 }));
                    }}
                    className={`flex-1 ${inputBg} border ${cardBorder} rounded-lg px-3 py-2 ${textPrimary} text-sm focus:border-purple-500 focus:outline-none`}
                  />
                  <input
                    type="date"
                    value={dateRange.end}
                    onChange={(e) => {
                      setDateRange(prev => ({ ...prev, end: e.target.value }));
                      setPagination(prev => ({ ...prev, offset: 0 }));
                    }}
                    className={`flex-1 ${inputBg} border ${cardBorder} rounded-lg px-3 py-2 ${textPrimary} text-sm focus:border-purple-500 focus:outline-none`}
                  />
                </div>

                {/* Clear Filters */}
                <button
                  onClick={() => {
                    setSearchQuery('');
                    setStatusFilter('');
                    setDateRange({ start: '', end: '' });
                    setPagination(prev => ({ ...prev, offset: 0 }));
                  }}
                  className={`${textMuted} hover:${textPrimary} text-sm transition-colors`}
                >
                  Clear filters
                </button>
              </div>
            </div>
          )}

          {/* Transaction List */}
          <div className="divide-y divide-transparent">
            {loading ? (
              <div className="p-12 text-center">
                <RefreshCcw className={`w-8 h-8 ${textMuted} animate-spin mx-auto mb-3`} />
                <p className={textMuted}>Loading transactions...</p>
              </div>
            ) : filteredTransactions.length === 0 ? (
              <div className="p-12 text-center">
                <AlertCircle className={`w-12 h-12 ${textMuted} mx-auto mb-3`} />
                <h3 className={`font-medium ${textPrimary} mb-1`}>No transactions found</h3>
                <p className={`${textMuted} text-sm`}>
                  {searchQuery || statusFilter || dateRange.start 
                    ? 'Try adjusting your filters'
                    : 'Your transactions will appear here'}
                </p>
              </div>
            ) : (
              filteredTransactions.map((transaction) => (
                <TransactionRow 
                  key={transaction.id} 
                  transaction={transaction} 
                  isDark={isDark}
                />
              ))
            )}
          </div>

          {/* Pagination */}
          {filteredTransactions.length > 0 && (
            <div className={`flex items-center justify-between p-4 border-t ${cardBorder}`}>
              <p className={`${textMuted} text-sm`}>
                Showing {pagination.offset + 1}-{Math.min(pagination.offset + pagination.limit, pagination.total)} of {pagination.total}
              </p>
              <div className="flex gap-2">
                <button
                  onClick={() => handlePageChange('prev')}
                  disabled={pagination.offset === 0}
                  className={`p-2 rounded-lg ${isDark ? 'hover:bg-white/10' : 'hover:bg-gray-100'} disabled:opacity-50 disabled:cursor-not-allowed`}
                >
                  <ChevronLeft className={`w-5 h-5 ${textMuted}`} />
                </button>
                <button
                  onClick={() => handlePageChange('next')}
                  disabled={!pagination.has_more}
                  className={`p-2 rounded-lg ${isDark ? 'hover:bg-white/10' : 'hover:bg-gray-100'} disabled:opacity-50 disabled:cursor-not-allowed`}
                >
                  <ChevronRight className={`w-5 h-5 ${textMuted}`} />
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default TransactionHistory;
